import { useState, useEffect, useMemo, useRef, Key, JSX } from 'react';
import { PageProps } from '@inertiajs/inertia';
import { Head, usePage, router } from '@inertiajs/react';
import { useUser } from '../../src/context/UserContext';
import { useForm } from '@mantine/form';
import {
  Container,
  TextInput,
  Radio,
  RadioGroup,
  Button,
  MantineProvider,
  createTheme,
  Box,
  Paper,
  Title,
  MultiSelect,
  Accordion,
  Text,
  Avatar,
  Stack,
  Select,
  Alert,
  SegmentedControl,
  ActionIcon,
  Group,
  InputLabel,
  Badge,
  LoadingOverlay,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { Notifications, showNotification } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconLink,
  IconBrandInstagram,
  IconBrandFacebook,
  IconTicket,
  IconMapPinFilled,
  IconCalendarTime,
  IconCalendarPlus,
  IconList,
  IconUsersGroup,
  IconInfoCircle,
  IconClock,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor, EditorContent } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import timezoneData from '../../entrypoints/utilities/timezones.json';
import ImageUploadModal from '../../components/ImageUploadModal';
import DangerZoneCard from '../../components/DangerZoneCard';
import { format } from 'date-fns-tz';
import AddressAutocomplete from '../../components/AddressAutocompleteMap';
import { parseISO } from 'date-fns';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../../styles/Login-form.css';
import '@mantine/tiptap/styles.css';
import '@mantine/dates/styles.css';

// Type definitions remain the same
type Artist = {
  id: number;
  name: string;
  avatar_url: string;
};

interface User {
  id: number;
  email: string;
  username: string;
  avatar_url: string | null;
  roles: string[];
  organizer_ids?: number[];
}

interface EventType {
  id: number;
  name: string;
}

interface DanceStyle {
  id: number;
  name: string;
}

interface EventData {
  id: number;
  name: string;
  slug?: string;
  description?: string;
  address?: string;
  online_event?: boolean;
  is_recurring?: boolean;
  spans_next_day?: boolean;
  timezone?: string;
  area_id: number | null;
  event_start_date?: string;
  event_end_date?: string;
  website_url?: string;
  ticket_url?: string;
  facebook_url?: string;
  event_type_ids?: number[];
  dance_style_ids?: number[];
  event_frequency_id?: number;
  tiktok_url: string;
  meetup_url: string;
  instagram_url: string;
  event_image_url: string | null;
  artists_ids?: number[];
  user_id?: number;
  event_status_id?: number;
  organizer_ids?: number[];
  event_image?: File | null;
  event_start_time?: string;
  event_end_time?: string;
}

interface EventFrequency {
  id: number;
  name: string;
}

interface RecurringEvent {
  id: number;
  title: string;
}

type OrganizerTuple = [string, number];

interface EditEventPageProps extends InertiaPageProps, PageProps {
  event: {
    id: number;
    name: string;
    slug?: string;
    description?: string;
    address?: string;
    online_event?: boolean;
    is_recurring?: boolean;
    spans_next_day?: boolean;
    timezone?: string;
    event_start_date?: string;
    event_end_date?: string;
    event_start_time?: string;
    event_end_time?: string;
    website_url?: string;
    ticket_url?: string;
    facebook_url?: string;
    event_type_ids?: number[];
    dance_style_ids?: number[];
    event_frequency_id?: number;
    tiktok_url: string;
    meetup_url: string;
    instagram_url: string;
    event_image_url: string;
    artists?: Artist[];
    artists_ids?: number[];
    dance_style?: DanceStyle[];
    recurring_event?: RecurringEvent;
    user_id?: number;
    event_status_id?: number;
    organizer_ids?: number[];
  };
  areas: [string, number][];
  artists?: Artist[];
  event_types: EventType[];
  dance_styles: DanceStyle[];
  event_frequencies: EventFrequency[];
  organizers?: Array<[string, number]>;
  errors?: Record<string, string[]>;
  user?: Array<[string, number]>;
}

interface AddressInfo {
  address: string;
  lat: number;
  lng: number;
  timeZoneId: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onTimezoneSelect: (timeZoneId: string) => void;
}

const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C',
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: { fontFamily: 'Poppins, sans-serif' },
  components: {
    InputLabel: {
      styles: (theme: { fontSizes: { sm: any } }) => ({
        label: {
          color: '#FFF7ED', // Ensure this color applies
          fontSize: theme.fontSizes.sm, // Optional: Adjust size
          fontWeight: 500, // Optional: Adjust weight
        },
      }),
    },
    Input: {
      styles: () => ({
        input: {
          backgroundColor: '#686868', // Correctly sets input background
          color: '#F2F2F2', // Ensures text inside the input is visible
          borderColor: '#787878', // Optional: Adjust border color
          '&::placeholder': {
            color: '#fff', // Optional: Lighten placeholder text
          },
        },
      }),
    },
  },
});

export default function EditEvent() {
  // 1. Context Hooks
  const { user, refreshUser } = useUser() as { user: User | null; refreshUser: () => Promise<void> };
  const { props } = usePage<EditEventPageProps>();
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(true);
  const [timezoneOptions, setTimezoneOptions] = useState<{ value: string; label: string }[]>([]);
  const [timezone, setTimezone] = useState('');
  const [address, setAddress] = useState('');

  // 2. Destructure props
  const {
    event,
    errors,
    artists: propArtists,
    event_types: propEventTypes,
    dance_styles: propDanceStyles,
    event_frequencies: propEventFrequencies,
    users: propUsers,
    event_statuses: propEventStatuses,
    organizers: propOrganizers,
  } = props;

  const statusMapping: Record<number, { status: string; color: string; icon: JSX.Element }> = {
    1: { status: 'Active', color: 'green', icon: <IconCheck size={16} /> },
    2: { status: 'Pending', color: 'yellow', icon: <IconAlertTriangle size={16} /> },
    3: { status: 'Draft', color: 'blue', icon: <IconInfoCircle size={16} /> },
  };

  const eventStatus =
    event.event_status_id && statusMapping[event.event_status_id]
      ? statusMapping[event.event_status_id]
      : { status: 'Unknown', color: 'gray', icon: <IconInfoCircle size={16} /> };

  // 3. State Hooks
  const [isLoading, setIsLoading] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);

  const [seoImagePreview, setSeoImagePreview] = useState<string | null>(event.event_image_url);

  const [values, setValues] = useState<EventData>(event);
  const startTimeRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);

  const startPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => startTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );
  const endPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => endTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );

  const eventStartTime = event.event_start_date
    ? format(new Date(event.event_start_date), 'HH:mm', { timeZone: 'UTC' })
    : '';
  const eventEndTime = event.event_end_date ? format(new Date(event.event_end_date), 'HH:mm', { timeZone: 'UTC' }) : '';

  const DRAFT_STATUS_ID = 3;
  const isDraft = event.event_status_id === DRAFT_STATUS_ID;

  const getIanaTimezone = (timezoneLabel: string): string => {
    // Normalize the label: replace all whitespace with underscores
    const normalizedLabel = timezoneLabel.replace(/\s+/g, '_');

    // Try to find a match in your timezoneData (assuming your keys are in correct IANA format)
    const entry = Object.entries(timezoneData).find(([value, label]) => value === normalizedLabel);
    if (entry) {
      const [ianaTimezone] = entry;

      return ianaTimezone;
    } else {
      console.warn('⚠️ No match found, defaulting to UTC!');
      return 'UTC';
    }
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value; // e.g., "18:09"

    form.setFieldValue('event_start_time', timeValue); // ✅ Update form values

    setValues((prevValues) => ({
      ...prevValues,
      event_start_time: timeValue, // ✅ Ensure values are updated
    }));

    form.validateField('event_end_date');
    form.validateField('event_end_time');
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value; // e.g., "16:09"

    form.setFieldValue('event_end_time', timeValue); // ✅ Update form values

    setValues((prevValues) => ({
      ...prevValues,
      event_end_time: timeValue, // ✅ Ensure values are updated
    }));

    form.validateField('event_end_date');
    form.validateField('event_end_time');
  };

  function validateURL(input: string) {
    const urlPattern = /^(http:\/\/|https:\/\/)/i;
    return urlPattern.test(input);
  }

  const startDate = event.event_start_date ? new Date(event.event_start_date) : null;
  const endDate = event.event_end_date ? new Date(event.event_end_date) : null;

  const getDatePortion = (isoString: string) => {
    if (!isoString) return null;
    // Extract just the YYYY-MM-DD portion
    return isoString.split('T')[0];
  };

  // 5. Form Hook
  const form = useForm({
    initialValues: {
      name: event.name || '',
      slug: event.slug || '',
      area_id: event.area?.id || null,
      description: event.description || '',
      address: event.address || '',
      timezone: event.timezone || '',
      online_event: event.online_event ? 'true' : 'false',
      is_recurring: event.is_recurring ? 'true' : 'false',
      spans_next_day: event.spans_next_day ? 'true' : 'false',
      event_start_date: event.event_start_date,
      event_start_time: event.event_start_time,
      event_end_time: event.event_end_time,
      event_end_date: event.event_end_date ? getDatePortion(event.event_end_date) : null,
      website_url: event.website_url || '',
      ticket_url: event.ticket_url || '',
      facebook_url: event.facebook_url || '',
      instagram_url: event.instagram_url || '',
      meetup_url: event.meetup_url || '',
      tiktok_url: event.tiktok_url || '',
      event_type_ids: event.event_type_ids || [],
      artistIds: event.artist_ids || [],
      dance_style_ids: event.dance_style_ids || [],
      event_frequency_id: event.event_frequency_id || '',
      user_id: event.user_id || null,
      event_status_id: event.event_status_id || null,
      organizer_ids: event.organizer_ids || [],
      event_image_url: event.event_image_url || null,
    },
    validate: {
      name: (value) => (value ? null : 'Event name is required'),
      slug: (value) => (value ? null : 'Slug is required'),
      description: (value) => {
        // If using the editor instance
        if (editor) {
          // Get plain text content (strips HTML tags)
          const plainText = editor.getText();

          // Check if it meets the minimum length requirement
          if (plainText.length < 20) {
            return 'Description must be at least 20 characters long';
          }
        }
        return null;
      },
      event_start_date: (value) => (value ? null : 'Event start date is required'),
      event_start_time: (value) => (value ? null : 'Event start time is required'),
      event_end_date: (value, values) => {
        if (!value || !values.event_start_date) return null;
        const start = new Date(values.event_start_date);
        const end = new Date(value);
        if (end < start) {
          return 'Event end date must be the same as or after the event start date';
        }
        return null;
      },
      event_end_time: (value, values) => {
        if (!value || !values.event_start_time || !values.event_start_date || !values.event_end_date) {
          return null;
        }

        const [startHour, startMinute] = values.event_start_time.split(':').map(Number);
        const [endHour, endMinute] = value.split(':').map(Number);

        // Parse dates for comparison
        let startDate, endDate;

        // Create consistent date objects for comparison
        if (values.event_start_date.includes('T')) {
          // Parse ISO timestamp
          const tempStartDate = new Date(values.event_start_date);
          startDate = new Date(tempStartDate.getUTCFullYear(), tempStartDate.getUTCMonth(), tempStartDate.getUTCDate());
        } else {
          // Parse YYYY-MM-DD format
          const [startYear, startMonth, startDay] = values.event_start_date.split('-').map(Number);
          startDate = new Date(startYear, startMonth - 1, startDay);
        }

        if (values.event_end_date.includes('T')) {
          // Parse ISO timestamp
          const tempEndDate = new Date(values.event_end_date);
          endDate = new Date(tempEndDate.getUTCFullYear(), tempEndDate.getUTCMonth(), tempEndDate.getUTCDate());
        } else {
          // Parse YYYY-MM-DD format
          const [endYear, endMonth, endDay] = values.event_end_date.split('-').map(Number);
          endDate = new Date(endYear, endMonth - 1, endDay);
        }

        // Compare dates without time components
        const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        // Check if dates are the same (comparing just the date portion)
        if (startDateOnly.getTime() === endDateOnly.getTime()) {
          // Same day: End time must be after start time
          if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
            return 'Event end time must be after event start time when the dates are the same';
          }
        } else if (endDateOnly < startDateOnly) {
          // End date before start date
          return 'Event end date cannot be before event start date';
        }
        // If dates are different and end date is after start date, any end time is valid

        return null;
      },
      timezone: (value) => (value ? null : 'Timezone is required'),
      website_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid website URL starting with http:// or https://';
        }
        return null;
      },
      ticket_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid passes URL starting with http:// or https://';
        }
        return null;
      },
      facebook_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid Facebook URL starting with http:// or https://';
        }
        return null;
      },
      instagram_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid Instagram URL starting with http:// or https://';
        }
        return null;
      },
      meetup_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid Meetup URL starting with http:// or https://';
        }
        return null;
      },
      tiktok_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid TikTok URL starting with http:// or https://';
        }
        return null;
      },
      event_type_ids: (value) =>
        Array.isArray(value) && value.length > 0 ? null : 'At least one event type is required',
      dance_style_ids: (value) =>
        Array.isArray(value) && value.length > 0 ? null : 'At least one dance style is required',
    },
    validateInputOnChange: true,
  });

  // Helper function to format GMT offset
  const formatOffset = (offsetSeconds: number) => {
    const hours = Math.floor(Math.abs(offsetSeconds) / 3600);
    const minutes = Math.floor((Math.abs(offsetSeconds) % 3600) / 60);
    const sign = offsetSeconds >= 0 ? '+' : '-';
    return `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const fetchTimezones = async () => {
      setIsLoadingTimezones(true);
      try {
        const apiKey = 'N2IE9BKU2EQR'; // Your TimeZoneDB API key
        const response = await fetch(`https://api.timezonedb.com/v2.1/list-time-zone?key=${apiKey}&format=json`);
        const data = await response.json();

        if (data.status === 'OK' && data.zones && Array.isArray(data.zones)) {
          // Format the timezone options
          const options = data.zones.map((zone: any) => ({
            value: zone.zoneName, // IANA timezone ID (e.g., "America/Chicago")
            label: `${zone.zoneName} (${formatOffset(zone.gmtOffset)})`, // Display name with GMT offset
          }));

          setTimezoneOptions(options);

          // Check if the current event timezone exists in the fetched timezones
          // If it does, make sure it's in the correct format
          if (form.values.timezone) {
            const matchedTimezone = options.find((option) => option.value === form.values.timezone);
            if (matchedTimezone) {
              // Timezone exists in the list, update it to ensure correct format
              form.setFieldValue('timezone', matchedTimezone.value);
            }
          }
        } else {
          console.error('Error fetching timezone list:', data.message || 'Unknown error');
        }
      } catch (error) {
        console.error('Failed to fetch timezones:', error);
      } finally {
        setIsLoadingTimezones(false);
      }
    };

    fetchTimezones();
  }, []);

  useEffect(() => {
    if (event) {
      console.log('Event image URL:', event.event_image_url);
      setSeoImagePreview(event.event_image_url);
    }
  }, [event]);

  useEffect(() => {
    if (user) {
      setIsLoading(false);
    }

    console.log('Event data received:', event);
    console.log('Form initial values:', form.values);

    if (Object.keys(form.errors).length > 0) {
      const firstErrorKey = Object.keys(form.errors)[0];
      const firstErrorMessage = form.errors[firstErrorKey];

      showNotification({
        title: 'Validation Error',
        message: firstErrorMessage || 'Please fix the errors before submitting.',
        color: 'red',
        icon: <IconAlertCircle />,
        autoClose: 5000,
        position: 'top-right',
      });
    }
  }, [user, form.errors]);

  // 6. Editor Hook
  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Heading.configure({ levels: [1, 2, 3] }),
      Link.configure({ openOnClick: true }),
    ],
    content: form.values.description,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();

      form.setFieldValue('description', html);
    },
  });

  // 7. Derived State and Data Processing
  const event_types = Array.isArray(propEventTypes) ? propEventTypes : [];
  const artists = Array.isArray(propArtists) ? propArtists : [];
  const dance_styles = Array.isArray(propDanceStyles) ? propDanceStyles : [];
  const event_frequencies = Array.isArray(propEventFrequencies) ? propEventFrequencies : [];
  const users = Array.isArray(propUsers) ? propUsers : [];
  const event_statuses = Array.isArray(propEventStatuses) ? propEventStatuses : [];
  const filteredOrganizers = useMemo(() => {
    const organizers = user?.roles?.includes('SuperAdmin')
      ? propOrganizers || []
      : (propOrganizers || []).filter((org) => user?.organizer_ids?.includes(org.id));

    // Sort organizers alphabetically by name
    return [...organizers].sort((a, b) => a.name.localeCompare(b.name));
  }, [propOrganizers, user]);

  const is_superadmin = user?.roles?.includes('SuperAdmin') || false;

  const frequencyOrder = ['One Time', 'Weekly', 'Every Two Weeks', 'Monthly', 'Annual'];
  const sortedEventFrequencies = event_frequencies.sort((a, b) => {
    return frequencyOrder.indexOf(a.name) - frequencyOrder.indexOf(b.name);
  });

  const [croppedImage, setCroppedImage] = useState<File | null>(null);

  const handleImageChange = (file: File) => {
    // Update your local component state:
    setValues((prevValues) => {
      const newValues = { ...prevValues, event_image: file };

      return newValues;
    });

    // Also update the form's state:
    form.setFieldValue('event_image', file);

    // Update preview:
    setSeoImagePreview(URL.createObjectURL(file));
  };

  const handleTimezoneChange = (selectedLabel: string) => {
    const ianaTimezone = getIanaTimezone(selectedLabel); // Convert label to IANA timezone

    if (!ianaTimezone || ianaTimezone.length < 3) {
      console.error('🚨 Error: Invalid timezone selected!', ianaTimezone);
      return;
    }

    // Update state and form as a string, not an array
    setValues((prevValues) => ({
      ...prevValues,
      timezone: ianaTimezone,
    }));

    form.setFieldValue('timezone', ianaTimezone);
  };

  const TIMEZONE_OPTIONS = Object.entries(timezoneData).map(([value, label]) => ({
    value,
    label: `${label} (${value})`,
  }));

  const handleSubmit = async (submittedValues: typeof form.values & { event_image?: File; draft?: boolean }) => {
    setIsLoading(true);
    console.log('isLoading set to true');
    setOverlayVisible(true);
    console.log('overlayVisible set to true');

    console.log('State is Loading:', isLoading);
    console.log('State Overlay Visible:', overlayVisible);

    // Check if this is a draft submission (either from current status or from button click)
    const isDraft =
      submittedValues.draft === true ||
      eventStatus.status === 'Draft' ||
      form.values.event_status_id === DRAFT_STATUS_ID;

    try {
      // Get CSRF Token
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      if (!csrfToken) {
        console.error('CSRF token not found');
        return;
      }

      // ✅ Build FormData for Submission
      const formData = new FormData();

      // For drafts, include all event information regardless of completeness
      // For non-drafts, everything should already be validated

      // Handle date fields safely
      if (form.values.event_start_date && form.values.event_start_time) {
        const rawStartDate = form.values.event_start_date;
        // Safe string operations with null checks
        const startDatePortion =
          rawStartDate && typeof rawStartDate === 'string' && rawStartDate.includes('T')
            ? rawStartDate.split('T')[0]
            : rawStartDate;

        const combinedStart = `${startDatePortion}T${form.values.event_start_time}:00`;
        formData.append('event[event_start_date]', combinedStart);
      } else if (form.values.event_start_date) {
        // If only date exists but no time, still save the date
        formData.append('event[event_start_date]', form.values.event_start_date);
      }

      if (form.values.event_end_date && form.values.event_end_time) {
        const rawEndDate = form.values.event_end_date;
        // Safe string operations with null checks
        const endDatePortion =
          rawEndDate && typeof rawEndDate === 'string' && rawEndDate.includes('T')
            ? rawEndDate.split('T')[0]
            : rawEndDate;

        const combinedEnd = `${endDatePortion}T${form.values.event_end_time}:00`;
        formData.append('event[event_end_date]', combinedEnd);
      } else if (form.values.event_end_date) {
        // If only date exists but no time, still save the date
        formData.append('event[event_end_date]', form.values.event_end_date);
      }

      // Always include timezone if it exists, even for drafts
      if (form.values.timezone) {
        if (Array.isArray(form.values.timezone)) {
          formData.append('event[timezone]', form.values.timezone[0]);
        } else {
          formData.append('event[timezone]', form.values.timezone);
        }
      }

      // Always include all form values for both drafts and submissions
      // Required fields
      formData.append('event[name]', form.values.name);
      if (form.values.slug) formData.append('event[slug]', form.values.slug);
      if (form.values.address) formData.append('event[address]', form.values.address);

      // Append description from the editor if it exists
      if (editor && typeof editor.getHTML === 'function') {
        formData.append('event[description]', editor.getHTML());
      }

      // Append event image if provided
      if (submittedValues.event_image) {
        formData.append('event[event_image]', submittedValues.event_image);
      }

      // Handle cropped image if available
      if (croppedImage) {
        formData.append('event[cropped_image]', croppedImage);
      }

      // Append optional fields - always include any provided values
      ['website_url', 'ticket_url', 'facebook_url', 'instagram_url', 'meetup_url', 'tiktok_url'].forEach((field) => {
        if (form.values[field]) formData.append(`event[${field}]`, form.values[field]);
      });

      // Boolean fields - always include
      formData.append('event[online_event]', form.values.online_event === 'true' ? 'true' : 'false');
      formData.append('event[is_recurring]', form.values.is_recurring === 'true' ? 'true' : 'false');
      formData.append('event[spans_next_day]', form.values.spans_next_day === 'true' ? 'true' : 'false');

      // ✅ Append Arrays Properly (Each Item Individually) - include any provided values
      if (Array.isArray(form.values.event_type_ids) && form.values.event_type_ids.length > 0) {
        form.values.event_type_ids.forEach((id) => formData.append('event[event_type_ids][]', id.toString()));
      }

      if (Array.isArray(form.values.dance_style_ids) && form.values.dance_style_ids.length > 0) {
        form.values.dance_style_ids.forEach((id) => formData.append('event[dance_style_ids][]', id.toString()));
      }

      if (Array.isArray(form.values.artistIds) && form.values.artistIds.length > 0) {
        form.values.artistIds.forEach((id) => formData.append('event[artist_ids][]', id.toString()));
      }

      if (Array.isArray(form.values.organizer_ids)) {
        if (form.values.organizer_ids.length > 0) {
          form.values.organizer_ids.forEach((id) => formData.append('event[organizer_ids][]', id.toString()));
        } else {
          // Send an empty array signal to Rails
          formData.append('event[organizer_ids][]', '');
        }
      }

      if (form.values.area_id) {
        formData.append('event[area_id]', form.values.area_id);
      }

      // Append parameters based on submission type
      if (isDraft) {
        formData.append('save_changes', 'true');
      } else {
        formData.append('submit_for_approval', 'true');
      }

      // Send PUT request
      const response = await fetch(`/events/${props.event.id}`, {
        method: 'PUT',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        showNotification({
          title: 'Update Successful',
          message: isDraft ? 'Event draft was successfully saved!' : 'Event was successfully updated!',
          color: 'green',
          icon: <IconCheck />,
          autoClose: 6000,
          position: 'top-right',
        });

        setTimeout(() => (window.location.href = `/events/${form.values.slug}`), 1500);
      } else {
        const data = await response.json().catch(() => ({}));

        showNotification({
          title: 'Submission Failed',
          message: data.error || 'An error occurred while updating the event.',
          color: 'red',
          icon: <IconAlertCircle />,
          autoClose: 5000,
          position: 'top-right',
        });

        if (data.errors) form.setErrors(data.errors);
      }
    } catch (error) {
      showNotification({
        title: 'Unexpected Error',
        message: 'An unexpected error occurred. Please try again.',
        color: 'red',
        icon: <IconAlertCircle />,
        autoClose: 5000,
        position: 'top-right',
      });

      console.error('Error updating event:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddressChange = (newAddress: string) => {
    form.setFieldValue('address', newAddress);
    setValues((prevValues) => ({
      ...prevValues,
      address: newAddress,
    }));
  };

  return (
    <MantineProvider theme={theme}>
      <Notifications />
      <Container size="sm" style={{ minHeight: '75vh' }}>
        <Paper p="lg" shadow="xs" style={{ borderRadius: '8px', backgroundColor: '#3d3c3e' }} pos="relative">
          {is_superadmin && (
            <Paper
              shadow="md"
              p="md"
              mb="lg"
              style={{
                backgroundColor: 'gray',
                borderColor: '#D6D6D6',
                borderRadius: '8px',
                paddingTop: '0rem',
              }}
            >
              <Stack gap="xs">
                <Group gap="xs">
                  <Text fw={700} size="xl" style={{ color: '#FFF7ED' }}>
                    Admin Info
                  </Text>
                  <Badge color="orange" variant="filled" size="lg">
                    ID: {event.id}
                  </Badge>
                </Group>

                {/* Select User */}
                <Select
                  label="Which user is claiming this event?"
                  name="user_id"
                  styles={{ label: { color: '#FFF7ED' } }}
                  data={users.map(([username, id]) => ({
                    value: id.toString(),
                    label: username,
                  }))}
                  value={form.values.user_id ? form.values.user_id.toString() : null}
                  onChange={(value) => form.setFieldValue('user_id', value ? Number(value) : null)}
                  searchable
                  clearable
                />

                {/* Select Event Status */}
                <Select
                  label="What is the status of this event?"
                  name="event_status_id"
                  styles={{ label: { color: '#FFF7ED' } }}
                  data={event_statuses.map((status) => ({
                    value: status.id.toString(),
                    label: status.name,
                  }))}
                  value={form.values.event_status_id ? form.values.event_status_id.toString() : null}
                  onChange={(value) => form.setFieldValue('event_status_id', value ? Number(value) : null)}
                  clearable
                />
              </Stack>
            </Paper>
          )}
          <Alert
            icon={eventStatus.icon}
            title={`Event Status: ${eventStatus.status}`}
            color={eventStatus.color}
            variant="filled"
            mb="md"
          ></Alert>
          {event.recurring_event && (
            <Alert
              icon={<IconInfoCircle size="1.5rem" />}
              title="Editing a Recurring Event Instance"
              color="orange"
              radius="sm"
              variant="filled"
              mb="md"
              mt="md"
            >
              <Text>
                You are currently editing a single instance of the recurring event:{' '}
                <strong>{event.recurring_event.title}</strong>.
              </Text>
              <Text mt="sm">
                These changes will <strong>not</strong> affect the other occurrences of this event.
              </Text>
            </Alert>
          )}
          <ImageUploadModal previewUrl={seoImagePreview} onImageChange={handleImageChange} />
          <Title order={3} c="#E4E4E7">
            Edit Event: {event.name}
          </Title>
          <form onSubmit={form.onSubmit(() => handleSubmit({ ...form.values, event_image: values.event_image }))}>
            <Accordion multiple className="form-accordion">
              <Accordion.Item value="event-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconCalendarPlus size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Event Host & Name</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    label="Event Host"
                    hidePickedOptions
                    placeholder={
                      form.values.organizer_ids && form.values.organizer_ids.length > 0 ? '' : 'Select event organizers'
                    }
                    name="organizer_ids"
                    data={filteredOrganizers.map((org) => ({
                      value: org.id.toString(),
                      label: org.name,
                    }))}
                    value={(form.values.organizer_ids || []).map((id) => id.toString())}
                    onChange={(values) => {
                      const numericValues = Array.isArray(values) ? values.map((v) => Number(v)) : [];
                      form.setFieldValue('organizer_ids', numericValues);
                    }}
                    searchable
                    clearable
                    mt="md"
                    styles={{ label: { color: '#FFF7ED' } }}
                  />
                  <TextInput
                    label="Event Name"
                    name="name"
                    placeholder="Enter the name"
                    {...form.getInputProps('name')}
                    mt="md"
                    required
                    styles={{ label: { color: '#FFF7ED' } }}
                  />

                  <TextInput
                    label="Slug"
                    name="slug"
                    placeholder="your-event-slug"
                    {...form.getInputProps('slug')}
                    mt="md"
                    required
                    styles={{ label: { color: '#FFF7ED' } }}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="location-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconMapPinFilled size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Location Info</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm" fw={500} mt={3} style={{ color: '#FFF7ED' }}>
                    Is this an online event or in-person event?
                  </Text>
                  <SegmentedControl
                    name="online_event"
                    color="orange"
                    transitionDuration={300}
                    transitionTimingFunction="linear"
                    size="sm"
                    data={[
                      { label: 'In Person', value: 'false' },
                      { label: 'Online', value: 'true' },
                    ]}
                    {...form.getInputProps('online_event')}
                    style={{
                      border: '2px solid orange',
                      backgroundColor: 'white',
                    }}
                  />

                  {form.values.online_event === 'false' && (
                    <AddressAutocomplete
                      value={form.values.address}
                      onChange={(newAddress) => {
                        setAddress(newAddress);
                        form.setFieldValue('address', newAddress);
                      }}
                      onTimezoneSelect={(newTimezone) => {
                        setTimezone(newTimezone);
                        form.setFieldValue('timezone', newTimezone);
                      }}
                      timezoneValue={form.values.timezone}
                    />
                  )}
                  <Select
                    label="Area"
                    placeholder="Select the event area"
                    {...form.getInputProps('area_id')}
                    data={(props.areas || []).map(([name, id]) => ({ value: id.toString(), label: name }))}
                    searchable
                    clearable
                    mt="md"
                    value={form.values.area_id ? form.values.area_id.toString() : null}
                    styles={{ label: { color: '#FFF7ED' } }}
                    onChange={(val) => form.setFieldValue('area_id', val ? Number(val) : null)}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="date-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconCalendarTime size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Date & Time Info</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <SegmentedControl
                    name="is_recurring"
                    color="orange"
                    transitionDuration={500}
                    transitionTimingFunction="linear"
                    size="sm"
                    disabled
                    data={[
                      { label: 'Single Event', value: 'false' },
                      { label: 'Recurring Event', value: 'true' },
                    ]}
                    {...form.getInputProps('is_recurring')}
                    style={{
                      border: '2px solid orange',
                      backgroundColor: 'white',
                    }}
                  />

                  <Group gap="md" mt="md" grow>
                    <DatePickerInput
                      label="Event Start Date"
                      placeholder="Select date"
                      name="event_start_date"
                      value={
                        form.values.event_start_date
                          ? (() => {
                              console.log('Raw event_start_date value:', form.values.event_start_date);
                              console.log('Value type:', typeof form.values.event_start_date);
                              console.log('Value length:', form.values.event_start_date.length);

                              // If the value is just a date string (YYYY-MM-DD), parse it properly
                              if (
                                form.values.event_start_date.length === 10 &&
                                form.values.event_start_date.includes('-')
                              ) {
                                // Create date at noon to avoid any timezone boundary issues
                                const [year, month, day] = form.values.event_start_date.split('-').map(Number);
                                console.log('Parsed components:', { year, month, day });
                                const date = new Date(year, month - 1, day, 12, 0, 0);
                                console.log('📆 Date created with noon local time:', date);
                                return date;
                              } else {
                                // Otherwise, use the standard Date constructor
                                console.log('Using standard Date constructor');
                                const date = new Date(form.values.event_start_date);
                                console.log('📆 Date created with standard constructor:', date);
                                return date;
                              }
                            })()
                          : null
                      }
                      onChange={(date) => {
                        if (date) {
                          const localDate = new Date(date);
                          localDate.setHours(0, 0, 0, 0);

                          // Get the date components directly from the local date object
                          const year = localDate.getFullYear();
                          const month = String(localDate.getMonth() + 1).padStart(2, '0');
                          const day = String(localDate.getDate()).padStart(2, '0');
                          const dateString = `${year}-${month}-${day}`;

                          // Store only the date portion, without time information
                          form.setFieldValue('event_start_date', dateString);
                        } else {
                          form.setFieldValue('event_start_date', '');
                        }
                        form.validateField('event_end_date');
                        form.validateField('event_end_time');
                      }}
                      required={!isDraft}
                      styles={{ label: { color: '#FFF7ED' } }}
                    />

                    <TimeInput
                      label="Choose the event start time"
                      name="event_start_time"
                      ref={startTimeRef}
                      rightSection={startPickerControl}
                      color="orange"
                      value={form.values.event_start_time}
                      onChange={(e) => {
                        handleStartTimeChange(e);

                        form.validateField('event_end_date');
                        form.validateField('event_end_time');
                      }}
                      required={!isDraft}
                      styles={{ label: { color: '#FFF7ED' } }}
                    />
                  </Group>

                  <Group gap="md" mt="md" grow>
                    <DatePickerInput
                      label="Event End Date"
                      name="event_end_date"
                      placeholder="Select date"
                      value={
                        form.values.event_end_date
                          ? (() => {
                              console.log('Raw event_end_date value:', form.values.event_end_date);

                              // Parse the date while respecting the UTC timezone
                              if (form.values.event_end_date.includes('T')) {
                                // If it has a 'T', it's a full ISO timestamp
                                const utcDate = new Date(form.values.event_end_date);

                                // Create a new date with just the UTC date components to avoid timezone shift
                                return new Date(
                                  utcDate.getUTCFullYear(),
                                  utcDate.getUTCMonth(),
                                  utcDate.getUTCDate(),
                                  12,
                                  0,
                                  0,
                                );
                              } else {
                                // If it's just YYYY-MM-DD
                                const [year, month, day] = form.values.event_end_date.split('-').map(Number);
                                return new Date(year, month - 1, day, 12, 0, 0);
                              }
                            })()
                          : null
                      }
                      onChange={(date) => {
                        if (date) {
                          // Extract UTC date components and create ISO date string (date only)
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          form.setFieldValue('event_end_date', `${year}-${month}-${day}`);
                        } else {
                          form.setFieldValue('event_end_date', '');
                        }
                      }}
                      required={!isDraft}
                      styles={{ label: { color: '#FFF7ED' } }}
                    />
                    <TimeInput
                      label="Choose the event end time"
                      name="event_end_time"
                      ref={endTimeRef}
                      rightSection={endPickerControl}
                      color="orange"
                      value={form.values.event_end_time}
                      onChange={(e) => {
                        handleEndTimeChange(e);

                        form.validateField('event_end_date');
                        form.validateField('event_end_time');
                      }}
                      required={!isDraft}
                      styles={{ label: { color: '#FFF7ED' } }}
                    />
                  </Group>
                  {(form.errors.event_end_date || form.errors.event_end_time) && (
                    <Alert variant="filled" color="red" mt="md" icon={<IconAlertCircle size={16} />}>
                      {form.errors.event_end_date && <div>{form.errors.event_end_date}</div>}
                      {form.errors.event_end_time && <div>{form.errors.event_end_time}</div>}
                    </Alert>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="event-details">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconList size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Event Details</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    label="What type of event is this?"
                    name="event_type_ids"
                    hidePickedOptions
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder={
                      form.values.event_type_ids && form.values.event_type_ids.length > 0
                        ? ''
                        : 'Choose your event types'
                    }
                    data={event_types.map((type) => ({
                      value: type.id.toString(), // Convert ID to string for MultiSelect
                      label: type.name,
                    }))}
                    value={
                      // Ensure we always have an array and convert numbers to strings
                      (Array.isArray(form.values.event_type_ids) ? form.values.event_type_ids : []).map((id) =>
                        id.toString(),
                      )
                    }
                    onChange={(values) => {
                      // Convert the selected string IDs back to numbers
                      const numericValues = values.map(Number);
                      form.setFieldValue('event_type_ids', numericValues);
                    }}
                    searchable
                    clearable
                    required={!isDraft}
                  />

                  <MultiSelect
                    label="Dance Styles"
                    name="dance_style_ids"
                    hidePickedOptions
                    className="select-dropdown"
                    styles={{
                      label: { color: '#FFF7ED' },
                      dropdown: { zIndex: 1000 }, // ✅ Increase dropdown z-index
                    }}
                    placeholder={
                      form.values.dance_style_ids && form.values.dance_style_ids.length > 0 ? '' : 'Choose dance styles'
                    }
                    data={dance_styles.map((style) => ({
                      value: style.id.toString(),
                      label: style.name,
                    }))}
                    value={(Array.isArray(form.values.dance_style_ids) ? form.values.dance_style_ids : []).map((id) =>
                      id.toString(),
                    )}
                    onChange={(values) => {
                      // Convert the selected string IDs back to numbers
                      const numericValues = values.map(Number);
                      form.setFieldValue('dance_style_ids', numericValues);
                    }}
                    searchable
                    clearable
                    mt="lg"
                    required={!isDraft}
                  />

                  <Select
                    label="Event Frequency"
                    name="event_frequency_id"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Select event frequency"
                    data={sortedEventFrequencies.map((freq) => ({
                      value: freq.id.toString(), // Mantine requires string values
                      label: freq.name, // Display frequency name
                    }))}
                    value={form.values.event_frequency_id ? form.values.event_frequency_id.toString() : null}
                    onChange={(value) => form.setFieldValue('event_frequency_id', value ? Number(value) : null)}
                    searchable
                    clearable
                    mt="lg"
                    mb="xl"
                    required={!isDraft}
                  />

                  <Text size="sm" fw={500} style={{ color: '#FFF7ED' }} required>
                    Event Description
                  </Text>
                  {editor && (
                    <RichTextEditor editor={editor} mt="">
                      <RichTextEditor.Toolbar>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Bold />
                          <RichTextEditor.Italic />
                          <RichTextEditor.Underline />
                          <RichTextEditor.Strikethrough />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Code />
                          <RichTextEditor.Highlight />
                          <RichTextEditor.ClearFormatting />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Link />
                          <RichTextEditor.Unlink />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.H1 />
                          <RichTextEditor.H2 />
                          <RichTextEditor.H3 />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Blockquote />
                          <RichTextEditor.Hr />
                          <RichTextEditor.BulletList />
                          <RichTextEditor.OrderedList />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Blockquote />
                          <RichTextEditor.OrderedList />
                          <RichTextEditor.BulletList />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Undo />
                          <RichTextEditor.Redo />
                        </RichTextEditor.ControlsGroup>
                      </RichTextEditor.Toolbar>
                      <RichTextEditor.Content />
                    </RichTextEditor>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="artist-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconUsersGroup size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Artists</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    label="Select Artists"
                    name="artistIds"
                    hidePickedOptions
                    placeholder="Choose artists"
                    styles={{
                      label: { color: '#FFF7ED', fontSize: '16px' }, // Label font size
                      input: { fontSize: '16px' }, // Input field font size
                      dropdown: { fontSize: '16px' }, // Dropdown items font size
                    }}
                    data={artists.map((artist) => ({
                      value: artist.id.toString(), // value as string
                      label: artist.name,
                    }))}
                    value={(form.values.artistIds || []).map(String)}
                    onChange={(values) => {
                      const numericValues = Array.isArray(values) ? values.map(Number) : [];
                      form.setFieldValue('artistIds', numericValues);
                    }}
                    searchable
                    clearable
                  />

                  {/* Display selected artist avatars below */}
                  {form.values.artistIds.length > 0 && (
                    <Stack mt="md">
                      <Group style={{ gap: '4px', flexWrap: 'wrap' }}>
                        {form.values.artistIds.map((id: Key | null | undefined) => {
                          const artist = artists.find((a) => a.id === id);
                          return artist ? (
                            <Avatar
                              key={id}
                              src={artist.avatar_url}
                              alt={artist.name}
                              radius="50%"
                              size="xl"
                              style={{
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                                border: '2px solid white',
                              }}
                            />
                          ) : null;
                        })}
                      </Group>
                    </Stack>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="website-links">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconLink size={20} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Links</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <TextInput
                    label="Website Link"
                    name="website_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the event website."
                    {...form.getInputProps('website_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Passes Link"
                    name="ticket_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to purchase passes."
                    {...form.getInputProps('ticket_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Facebook Event Link"
                    name="facebook_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to Facebook event."
                    {...form.getInputProps('facebook_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Instagram Link"
                    name="instagram_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to the event Instagram page."
                    {...form.getInputProps('instagram_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Meetup Link"
                    name="meetup_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to Meetup event."
                    {...form.getInputProps('meetup_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Tiktok Link"
                    name="tiktok_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to the event Tiktok event page."
                    {...form.getInputProps('tiktok_url')}
                    mt="md"
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            {/* Example error display */}
            {errors && Object.keys(errors).length > 0 && (
              <Box color="red" mt="md">
                {Object.entries(errors).map(([field, messages]) => (
                  <div key={field}>
                    {field}: {messages.join(', ')}
                  </div>
                ))}
              </Box>
            )}

            {event.event_status_id === 3 ? (
              <Group mt="xl" gap="md">
                <Button
                  variant="light"
                  color="blue"
                  size="lg"
                  fullWidth
                  onClick={() => {
                    // For draft, only validate name
                    if (!form.values.name || form.values.name.trim() === '') {
                      showNotification({
                        title: 'Validation Error',
                        message: 'Event name is required even for drafts.',
                        color: 'red',
                        icon: <IconAlertCircle />,
                        autoClose: 5000,
                      });
                      return;
                    }

                    // If name exists, proceed with draft submission
                    handleSubmit({
                      ...form.values,
                      event_image: values.event_image,
                      draft: true, // Add a flag to indicate this is a draft submission
                    });
                  }}
                >
                  Save Changes as Draft
                </Button>

                <Button
                  color="orange"
                  size="lg"
                  fullWidth
                  onClick={() => {
                    // For publish, run full validation
                    const validationErrors = form.validate();
                    if (validationErrors.hasErrors) {
                      // Clear any existing notifications first
                      Notifications.clean();

                      // Show a notification for each validation error with a slight delay
                      Object.entries(validationErrors.errors).forEach(([label, message], index) => {
                        // Format the label to be more readable
                        const formattedLabel = label
                          // Replace underscores with spaces
                          .replace(/_/g, ' ')
                          // Split into words, capitalize each one, and join back
                          .split(' ')
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ');

                        setTimeout(() => {
                          Notifications.show({
                            title: `Error with ${formattedLabel}`,
                            message: message,
                            color: 'red',
                            icon: <IconAlertCircle />,
                            autoClose: 8000,
                            position: 'top-right',
                          });
                        }, index * 200); // 200ms delay between each notification
                      });

                      setIsLoading(false);
                      return;
                    }

                    // If validation passes, proceed with publish submission
                    handleSubmit({
                      ...form.values,
                      event_image: values.event_image,
                      draft: false, // Flag to indicate this is not a draft submission
                    });
                  }}
                >
                  Submit Event For Approval
                </Button>
              </Group>
            ) : (
              <Button
                mt="xl"
                size="lg"
                fullWidth
                loading={isLoading}
                onClick={() => {
                  // If validation passes, proceed with update
                  handleSubmit({ ...form.values, event_image: values.event_image });
                }}
              >
                Update Event
              </Button>
            )}
          </form>
          {/* Danger Zone Section */}
          <div style={{ marginTop: '2rem' }}>
            <DangerZoneCard eventId={event.id} eventName={event.name} />
          </div>
        </Paper>
      </Container>
    </MantineProvider>
  );
}
